// ─── SCENE TAGGING ──────────────────────────────────────────────────────────
//
// One channel can hold two scenes at once. Haley, 2026-08-02:
//
//   "the ai should be responding to that scene that we are writing into if
//    that makes sense so things stay separate but can converge in a shared
//    scene when we want it to"
//
// and, defining the term:
//
//   "it's only when nicole and I's characters are interacting in the same
//    space that it's a shared scene"
//
// So a scene is NOT inferred from who spoke last — that heuristic
// (aiThreadOwner) is wrong every time the writers interleave, which in the
// Pack House channel is constantly. Instead:
//
//   • a writer's next message CONTINUES the scene they were last writing in
//   • convergence is EXPLICIT, because "when we want it to" is a decision,
//     not something to guess at
//   • the AI answers inside the scene of the message that triggered it
//
// The Pack House back-catalogue was read message by message and tagged by
// hand (scripts/tag-packhouse-scenes.py); this keeps new messages tagged the
// same way without anyone having to think about it.

import { looksOOC } from './director.js';
import { fetchSteppedWindow } from './historyWindow.js';

// NOTE: there is deliberately no isCoRpChannel() here. ChannelView already
// computes one, by CATEGORY NAME (isCoRpCategoryName), which covers every
// co-RP category rather than one hardcoded id. Exporting a second function of
// the same name shadowed that local const inside the component and shipped a
// white screen — "Ko is not a function" — because the JSX called the boolean.
// Use the component's own value.

/**
 * The scene this writer is currently writing in: their most recent tagged
 * message in the channel. Pure — `messages` is oldest-first.
 */
export function currentSceneForWriter(messages, userId) {
  for (let i = (messages || []).length - 1; i >= 0; i--) {
    const m = messages[i];
    if (!m || m.is_ai_character) continue;
    if (m.user_id !== userId) continue;
    if (m.scene_tag) return m.scene_tag;
  }
  return null;
}

/** Every distinct scene currently live in the channel, newest activity first. */
export function activeScenes(messages, { limit = 30 } = {}) {
  const seen = new Map();
  const list = messages || [];
  for (let i = list.length - 1; i >= 0 && seen.size < limit; i--) {
    const m = list[i];
    if (!m?.scene_tag || seen.has(m.scene_tag)) continue;
    seen.set(m.scene_tag, { tag: m.scene_tag, shared: !!m.scene_shared });
  }
  return [...seen.values()];
}

/** The newest scene anything was written into — what a reader is looking at. */
export function newestSceneInChannel(messages) {
  for (let i = (messages || []).length - 1; i >= 0; i--) {
    if (messages[i]?.scene_tag) return messages[i].scene_tag;
  }
  return null;
}

/**
 * Where this writer's next message lands if they don't pick a scene.
 *
 * ⚠️ `knownWriterScene` IS THE WHOLE POINT — pass it. Live failure, Pack House,
 * 2026-08-09 23:43. Haley's own scene is `diner-cruz` (95 of her messages, with
 * Cruz). Nicole had been writing `park-lance` with Lance all day. Haley posted
 * a reply to her Cruz thread and it was tagged `scene-1`, alone, where no
 * character could see it.
 *
 * The cause was not the scene logic. `currentSceneForWriter` scans the LOADED
 * MESSAGES, ChannelView loads the newest 30 on open, and Nicole had posted
 * exactly 32 since Haley's last message. Haley's own scene had fallen two
 * messages off the end of the page, so the scan found nothing of hers and the
 * fallback invented a scene. **Scene continuity was being decided by the UI's
 * pagination window** — the writer's history is in the database, not on screen.
 *
 * So callers resolve the writer's scene from the DB (`fetchWriterScene`) and
 * pass it here. `undefined` means "not resolved, scan what you have" — the
 * old behaviour, fine for tests and pure calls; `null` means "the DB says she
 * genuinely has no scene in this channel."
 *
 * Only in that genuine-null case does it fall through to the scene being read.
 * That fallback must NEVER outrank a real scene of her own: park-lance was the
 * newest activity that night, so a naive "join what's newest" would have filed
 * her Cruz dialogue into Nicole's Lance scene — contaminating a 440-message
 * thread and handing Lance her Cruz context. Wrong in a worse way than the bug.
 */
export function defaultSceneForWriter(messages, userId, { knownWriterScene } = {}) {
  const own = knownWriterScene !== undefined
    ? knownWriterScene
    : currentSceneForWriter(messages, userId);
  return own || newestSceneInChannel(messages) || nextSceneName(messages);
}

/**
 * THE WHOLE SCENE, from the database. Haley, 2026-08-09: *"it needs to be able
 * to see All the messages."*
 *
 * `scopeHistoryToScene` was written to carry a whole scene, and the constant
 * above it records the reasoning. But it only ever saw
 * what the VIEW had loaded, and ChannelView loads the newest 30 messages on
 * open. So in the Pack House channel, where `park-lance` alone is 440 messages,
 * a character answering in that scene received at most 30 channel messages,
 * filtered down to whatever slice of its own scene survived — while every cap
 * in the code reported that it was carrying the lot. The 400 was real; the
 * input never was.
 *
 * Scene isolation still holds: this asks the DB for the scene, not the channel.
 * Untagged rows are included for the same reason `scopeHistoryToScene` includes
 * them — a message with no tag predates tagging and belongs to everyone.
 */
export async function fetchSceneHistory(supabase, channelId, sceneTag, _limit = FULL_STORY_MSGS) {
  if (!supabase || !channelId) return null;
  // Only a tag this codebase generated ever reaches here, but the value is
  // interpolated into PostgREST's filter grammar, so anything with a comma or
  // a paren in it would change the shape of the query rather than the value.
  const safeTag = sceneTag && /^[A-Za-z0-9._-]+$/.test(sceneTag) ? sceneTag : null;
  // A stepped window (historyWindow.js). `_limit` is kept for the callers that
  // still pass one, but it was never honoured past 1,000 rows: PostgREST cut
  // the response there, and the newest-1,000 window that resulted slid on
  // every message, so a channel's history was never reusable from cache.
  const { data, error } = await fetchSteppedWindow(supabase, 'channel_messages', (q) => {
    const scoped = q.eq('channel_id', channelId);
    return safeTag ? scoped.or(`scene_tag.eq.${safeTag},scene_shared.is.true,scene_tag.is.null`) : scoped;
  });
  // supabase-js returns {data:null,error} rather than throwing.
  if (error) {
    console.error('[scenes] could not load scene history from DB:', error.message);
    return null;
  }
  return data || [];   // chronological, oldest first
}

/**
 * The writer's current scene as the DATABASE knows it, independent of how many
 * messages the view happens to have loaded. One indexed lookup on
 * (channel_id, user_id, created_at).
 */
export async function fetchWriterScene(supabase, channelId, userId) {
  if (!supabase || !channelId || !userId) return null;
  const { data, error } = await supabase
    .from('channel_messages')
    .select('scene_tag')
    .eq('channel_id', channelId)
    .eq('user_id', userId)
    .eq('is_ai_character', false)
    .not('scene_tag', 'is', null)
    .order('created_at', { ascending: false })
    .limit(1);
  // supabase-js does NOT throw on failure — it returns {data:null,error}. A
  // swallowed error here would silently reinstate the exact bug this fixes,
  // so it is surfaced and the caller falls back to scanning the page.
  if (error) {
    console.error('[scenes] could not resolve writer scene from DB:', error.message);
    return undefined;
  }
  return data?.[0]?.scene_tag || null;
}

/**
 * Decide the tags for a message this writer is about to send.
 *
 * Order: the explicit per-message pick → continuity with their own last
 * message → the scene they are reading → a first scene.
 *
 * `shared` is never guessed. It is true only when the writer has explicitly
 * joined a scene the OTHER writer is also in — both characters in the same
 * space, which is Haley's definition and nobody else's job to infer.
 */
export function tagsForOutgoingMessage({
  content, messages, userId, otherUserIds = [], explicitScene = null,
  knownWriterScene,
}) {
  // Haley chose a per-message control over a sticky header one: the scene is
  // decided as the message is sent. An explicit pick wins; otherwise the
  // message continues whatever she was last writing in, so only a CHANGE
  // costs a click. There is deliberately no stored "pending scene" — the
  // choice lives in the composer and is spent on the message it was made for.
  const scene = explicitScene || defaultSceneForWriter(messages, userId, { knownWriterScene });

  // Shared when the other writer has also been writing into this same scene.
  const otherIsHere = (messages || []).some(m =>
    m && !m.is_ai_character && m.scene_tag === scene && otherUserIds.includes(m.user_id));

  return {
    scene_tag: scene,
    scene_shared: otherIsHere,
    is_ooc: looksOOC(content),
  };
}

/**
 * The scene an AI reply belongs to: the scene of the message it is answering.
 * Falls back to the newest tagged message so a reply is never left untagged.
 */
export function sceneForAIReply(messages, sourceMsg) {
  if (sourceMsg?.scene_tag) {
    return { scene_tag: sourceMsg.scene_tag, scene_shared: !!sourceMsg.scene_shared };
  }
  for (let i = (messages || []).length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m?.scene_tag) return { scene_tag: m.scene_tag, scene_shared: !!m.scene_shared };
  }
  return { scene_tag: null, scene_shared: null };
}

/** A readable name for a brand-new scene, unique within the channel. */
export function nextSceneName(messages) {
  const used = new Set((messages || []).map(m => m?.scene_tag).filter(Boolean));
  for (let n = 1; n < 500; n++) {
    const tag = `scene-${n}`;
    if (!used.has(tag)) return tag;
  }
  return `scene-${used.size + 1}`;
}

// ⚠️ THESE WERE STILL TRUNCATING AFTER v244. Haley, 2026-08-09: "until the
// model receives 100% of what the code claims."
//
// v244 widened the INPUT (the loaded page) but left these budgets alone, and
// measured against real content they were still the binding constraint:
//   park-lance   440 msgs / 300,167 chars  -> 240,000 char budget cut ~20%
//   dm:vaughn    851 msgs / 184,118 chars  -> 400-message cap cut 53%
//   dm:ford   13,584 msgs / 3,223,999 chars-> 400-message cap cut 97%
// So a character in park-lance was still missing roughly ninety of its beats
// while the code said "400". Same class of bug as the one before it, one layer
// further in — which is the whole reason to measure rather than assume.
//
// SIZED FROM THE REAL MODEL, NOT A GUESS. Both writers run
// deepseek/deepseek-v4-flash via OpenRouter, whose advertised context is
// 1,048,576 tokens (queried live from the OpenRouter model list, not recalled).
// English prose runs ~4 chars/token, so 1M tokens ≈ 4M characters. Reserving
// roughly a third for the system prompt, memory blocks and the reply leaves
// ~2.4M characters of history — which covers every scene and conversation in
// the app except the 3.2M-char Ford DM.
//
// Haley, on cost: "we don't care about the cost of the tokens for it." Noted
// and taken at face value; the ceiling below exists to stay inside the MODEL's
// limit, not to save money. At v4-flash input pricing a 75k-token park-lance
// turn is well under a cent.
// Sized against the LARGEST SINGLE conversation in the app, measured:
//   dm ford (Nicole)  7,428 msgs / 1,872,000 chars ≈ 468k tokens
//   dm ford (Nicole)  6,156 msgs / 1,354,000 chars ≈ 339k tokens
//   channel park-lance  440 msgs /   300,167 chars ≈  75k tokens
// (The 3.2M figure that first appeared in this audit was two Ford conversations
//  summed — a reminder to check what a number is actually counting.)
// 8,000 messages covers every one of them whole, and the char ceiling keeps the
// largest inside the model's 1,048,576-token context with room for the system
// prompt and the reply.
export const FULL_STORY_MSGS = 8000;
export const FULL_STORY_CHARS = 2400000;

/**
 * The history a character in `currentScene` may see.
 *
 * ORDER MATTERS AND IS THE POINT. Callers used to cap to the last N messages
 * of the CHANNEL and let the scene filter run afterwards, so in a room with
 * two live threads a character received the last 60 messages of the room and
 * then lost half of them to the filter — roughly 30 beats of their own scene.
 * Haley, 2026-08-02: "memory has been very bad." Scene isolation was quietly
 * halving history depth. Filter first, cap second.
 *
 * DEPTH: Haley, 2026-08-02 — "the ai needs to know the Whole story", "not lose
 * details", "why don't you raise context window to the whole story it can
 * handle it". Measured on the real Pack House channel, the largest scene is
 * 116 messages / ~16k tokens, and docs/IMMERSION_ROADMAP.md already found
 * verbatim beats summarisation by 16-22 points. So the default now carries a
 * whole scene rather than a 60-message tail.
 *
 * `charBudget` is a safety valve, not a target: it only bites on a scene far
 * larger than anything played so far, and it fills NEWEST-FIRST so what it
 * drops is the oldest material — which is exactly what the scene summary
 * covers. A count cap alone would have silently truncated a long scene with
 * no warning, which is the failure mode this project keeps finding.
 */
// ⚠️ THE DEFAULTS ARE THE CONSTANTS. They used to be hardcoded `400` and
// `240000`, and when v245 raised the constants these were left behind — so
// ChannelView, which calls with THREE arguments, silently kept the old 240,000
// budget and park-lance stayed truncated by roughly 40% while every test and
// every log line reported it whole. contextCoverage.test.js passed straight
// over it because it exercises scopeFullHistory, which passes the constants
// explicitly. Two copies of one number, disagreeing, with the test measuring
// the copy that was right.
export function scopeHistoryToScene(messages, currentScene, limit = FULL_STORY_MSGS, charBudget = FULL_STORY_CHARS) {
  const all = messages || [];
  const scoped = currentScene
    ? all.filter(m => !m?.scene_tag || m.scene_tag === currentScene || m.scene_shared)
    : all;
  const capped = limit > 0 ? scoped.slice(-limit) : scoped;
  if (!charBudget || charBudget <= 0) return capped;

  let used = 0;
  const keptReversed = [];
  for (let i = capped.length - 1; i >= 0; i--) {
    const len = messageLength(capped[i]);
    if (used + len > charBudget && keptReversed.length) break;
    used += len;
    keptReversed.push(capped[i]);
  }
  return keptReversed.reverse();
}

// Channel and DM rows carry `content`; RP story messages carry `text`. Reading
// only `content` meant every RP message measured as ZERO, so the safety valve
// was inert on the one surface with no other bound — scopeFullHistory kept all
// 291 messages of Haley's live story (239,595 chars) even when handed a budget
// of 50,000. Measured 2026-08-10, while the RP prompt was at 164K tokens and
// climbing about 4K an hour.
//
// This does not change what ships today: 239,595 is far under FULL_STORY_CHARS
// (2,400,000), so nothing is dropped now. It makes the guard real, so a very
// long story is bounded instead of growing until the model's context ends it.
export function messageLength(m) {
  return String(m?.content ?? m?.text ?? '').length;
}

/** How much of a scene the caller actually got, for reporting a real cap. */
export function sceneHistoryStats(messages, currentScene, limit = FULL_STORY_MSGS, charBudget = FULL_STORY_CHARS) {
  const all = messages || [];
  const scoped = currentScene
    ? all.filter(m => !m?.scene_tag || m.scene_tag === currentScene || m.scene_shared)
    : all;
  const kept = scopeHistoryToScene(messages, currentScene, limit, charBudget);
  return { inScene: scoped.length, kept: kept.length, dropped: scoped.length - kept.length };
}

// The whole-story depth, shared by every surface. Haley, 2026-08-02: "the ai
// needs to know the Whole story ... and not lose details ... in any part of
// the app". Measured caps before this: story 25 messages, group chat 40, DM
// 60, channel 60. The story surface — the one with the longest arcs — had the
// smallest window of all.


/** Whole-conversation history with the same budget rules as a scene. */
export function scopeFullHistory(messages, limit = FULL_STORY_MSGS, charBudget = FULL_STORY_CHARS) {
  return scopeHistoryToScene(messages, null, limit, charBudget);
}
